import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import {
  fieldOfficerAlerts, officerLocations, resourceDeployment,
  emergencyUnits, departmentStatus
} from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveCopilotSection } from "../components/live/LiveResponseSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  CheckCircle, Clock, ChevronDown, ChevronUp, AlertTriangle,
  MapPin, User, Brain, Shield, Radio, Phone, MessageSquare,
  TrendingUp, Activity, Zap, Target, FileText, Users,
  Navigation, Star, BarChart2, AlertCircle, RefreshCw,
  Mic, Send, Eye, Lock, Award
} from "lucide-react";

// ── inline data ───────────────────────────────────────────────────
const SLA_TRACKER = [
  { id:"INC-8821", issue:"Crowd surge Ramkund Entry 2",       owner:"UNIT-P22",     slaAt:"14:50", minsLeft:17,  priority:"CRITICAL", status:"In Progress" },
  { id:"INC-8819", issue:"Ambulance access blocked — Camp B", owner:"UNIT-H05",   slaAt:"14:44", minsLeft:11,  priority:"CRITICAL", status:"Escalated"   },
  { id:"INC-8820", issue:"Water tanker missing — Panchvati",  owner:"UNIT-M43",     slaAt:"15:15", minsLeft:42,  priority:"HIGH",     status:"Pending"     },
  { id:"INC-8818", issue:"Shelter overload — Camp 3",         owner:"Revenue Dept",   slaAt:"16:00", minsLeft:87,  priority:"HIGH",     status:"Monitoring"  },
  { id:"INC-8817", issue:"Missing child — Panchvati Sec 7",   owner:"UNIT-P16",   slaAt:"15:00", minsLeft:27,  priority:"HIGH",     status:"Active"      },
  { id:"INC-8816", issue:"Heatstroke cluster — Zone D",       owner:"Medical Team",   slaAt:"Done",  minsLeft:999, priority:"HIGH",     status:"Resolved"    },
];

const OFFICER_PERFORMANCE = [
  { unit:"UNIT-P11",           rank:"Inspector",    zone:"Ramkund Ghat",   tasksToday:8,  completed:5, slaHit:87, incidents:3, status:"Responding", radio:"CH-4" },
  { unit:"UNIT-P24",          rank:"Constable",    zone:"Panchvati",       tasksToday:6,  completed:5, slaHit:92, incidents:2, status:"On Duty",    radio:"CH-3" },
  { unit:"UNIT-P07",          rank:"PSI",          zone:"Kalaram Temple",  tasksToday:5,  completed:4, slaHit:95, incidents:1, status:"On Duty",    radio:"CH-5" },
  { unit:"UNIT-H03",          rank:"CMO Health",   zone:"Trimbak Rd Camp", tasksToday:9,  completed:6, slaHit:78, incidents:3, status:"Emergency",  radio:"CH-2" },
  { unit:"UNIT-N15",          rank:"JE Municipal", zone:"Panchvati Zone",  tasksToday:4,  completed:3, slaHit:88, incidents:2, status:"In Progress",radio:"CH-6" },
  { unit:"UNIT-P31",          rank:"Inspector",    zone:"Nashik Road Stn", tasksToday:7,  completed:6, slaHit:94, incidents:1, status:"On Duty",    radio:"CH-4" },
  { unit:"UNIT-P08",          rank:"Head Const.",  zone:"Gangapur Camp",   tasksToday:4,  completed:4, slaHit:100,incidents:0, status:"On Duty",    radio:"CH-7" },
  { unit:"UNIT-P19",          rank:"Sub-Insp.",     zone:"Trimbakeshwar",   tasksToday:6,  completed:4, slaHit:82, incidents:2, status:"On Duty",    radio:"CH-5" },
];

const TASK_BOARD = {
  assigned:[
    { id:"TSK-482", task:"Deploy barricade — Ramkund Entry 2 west side",    assignee:"UNIT-P22",    priority:"HIGH",   due:"14:45", dept:"Police"   },
    { id:"TSK-483", task:"Confirm ORS stock at Panchvati Ghat Steps",       assignee:"UNIT-M43",    priority:"MED",    due:"15:00", dept:"NMC"      },
    { id:"TSK-484", task:"Pre-position ambulance AM-04 at Camp Beta",        assignee:"UNIT-H05",  priority:"HIGH",   due:"14:40", dept:"Health"   },
  ],
  inProgress:[
    { id:"TSK-479", task:"Crowd diversion — Gate 2 → Gate 4 active",        assignee:"UNIT-P22",    priority:"CRITICAL",due:"14:50",dept:"Police"   },
    { id:"TSK-480", task:"Missing child search — MP-1185, Panchvati Sec 7", assignee:"UNIT-P16",  priority:"HIGH",   due:"15:00", dept:"Police"   },
    { id:"TSK-481", task:"Water tanker reroute — TK-12 to Station 8",       assignee:"UNIT-M43",    priority:"HIGH",   due:"15:15", dept:"NMC"      },
  ],
  pendingEscalation:[
    { id:"TSK-478", task:"Ambulance route clearance — Trimbak Rd",          assignee:"UNIT-H05",  priority:"CRITICAL",due:"14:44",dept:"Health"   },
    { id:"TSK-477", task:"Shelter Camp Delta activation — Revenue Dept",    assignee:"Revenue Dept",  priority:"HIGH",   due:"16:00", dept:"Revenue"  },
  ],
  closed:[
    { id:"TSK-474", task:"Heatstroke cluster Zone D — all 6 stabilised",   assignee:"Medical MT-04", priority:"HIGH",   due:"Done",  dept:"Health"   },
    { id:"TSK-475", task:"Missing person MP-1182 reunited with family",     assignee:"UNIT-P16",  priority:"MED",    due:"Done",  dept:"Police"   },
    { id:"TSK-476", task:"Bridge congestion cleared — Police escort done",  assignee:"UNIT-P27",  priority:"MED",    due:"Done",  dept:"Police"   },
  ],
};

const AI_RECOMMENDATIONS = [
  { id:"REC-001", type:"Proactive", confidence:94, zone:"Ramkund",   msg:"Density reaching 9.2/m² — recommend pre-positioning 2 platoons at Entry 4 before 15:00 peak", urgency:"HIGH",   action:"Pre-position now" },
  { id:"REC-002", type:"Reactive",  confidence:91, zone:"Panchvati", msg:"Water demand will spike at Ghat Steps in 18 min — dispatch NMC tanker TK-14 now for zero-gap supply", urgency:"HIGH",   action:"Dispatch tanker" },
  { id:"REC-003", type:"Proactive", confidence:88, zone:"Trimbak",   msg:"Ambulance AM-03 route blocked — alternate route via NH3 link road saves 8 min clearance time",    urgency:"MEDIUM", action:"Reroute AM-03"   },
  { id:"REC-004", type:"Advisory",  confidence:86, zone:"Nashik Rd", msg:"Train 12166 arriving 14:42 — expected 3,200 pilgrims. Alert Nashik Road beat squad to Gate 3",     urgency:"MEDIUM", action:"Alert beat squad" },
  { id:"REC-005", type:"Advisory",  confidence:82, zone:"All Zones", msg:"UV Index 9 and temperature 38°C — ORS distribution should be increased by 40% in Ramkund zone",   urgency:"LOW",    action:"Increase ORS"    },
  { id:"REC-006", type:"Proactive", confidence:79, zone:"Gangapur",  msg:"Shelter utilisation at 44% — redirect overflow from Camp 3 to Gangapur Camp D (500 slots free)",  urgency:"LOW",    action:"Open Camp D"     },
];

const COMMS_LOG = [
  { time:"14:32", from:"UNIT-P22",      channel:"Radio CH-4", msg:"Diversion active at Entry 2. Requesting 1 more platoon from reserve.",          type:"field"   },
  { time:"14:30", from:"AI Copilot",      channel:"App Alert",  msg:"Recommend closing Entry 2 if density exceeds 9.5/m² in next 5 min.",            type:"ai"      },
  { time:"14:28", from:"UNIT-H05",    channel:"Radio CH-2", msg:"Ambulance AM-03 still blocked — need immediate police escort on Trimbak Road.",  type:"field"   },
  { time:"14:25", from:"Command Centre",  channel:"Broadcast",  msg:"All officers — peak crowd window 15:00–16:00. Maximum vigilance required.",       type:"command" },
  { time:"14:22", from:"UNIT-M43",     channel:"App",        msg:"Replacement tanker TK-12 dispatched. ETA Panchvati 15:12.",                      type:"field"   },
  { time:"14:18", from:"AI Copilot",      channel:"Alert",      msg:"Missing child MP-1185 — 3 possible matches in CCTV feed Sector 6 & 7.",          type:"ai"      },
  { time:"14:15", from:"UNIT-P16",    channel:"Radio CH-3", msg:"MP-1185 search underway — checking Sector 6 footage now.",                       type:"field"   },
  { time:"14:10", from:"AI Copilot",      channel:"Alert",      msg:"Heatstroke risk rising Zone D — recommend ORS deployment before 14:30.",         type:"ai"      },
];

const SHIFT_PERFORMANCE = [
  { time:"06:00", tasks:4,  completed:4,  sla:100, incidents:2 },
  { time:"07:00", tasks:8,  completed:7,  sla:96,  incidents:4 },
  { time:"08:00", tasks:14, completed:13, sla:93,  incidents:7 },
  { time:"09:00", tasks:18, completed:16, sla:91,  incidents:9 },
  { time:"10:00", tasks:22, completed:19, sla:88,  incidents:11},
  { time:"11:00", tasks:19, completed:18, sla:92,  incidents:8 },
  { time:"12:00", tasks:16, completed:15, sla:94,  incidents:7 },
  { time:"13:00", tasks:14, completed:13, sla:93,  incidents:6 },
  { time:"14:00", tasks:12, completed:10, sla:90,  incidents:5 },
];

const ZONE_COVERAGE = [
  { zone:"Ramkund Ghat",   officers:3, required:4, gap:1,  coverage:75, risk:"critical" },
  { zone:"Panchvati",      officers:2, required:3, gap:1,  coverage:67, risk:"high"     },
  { zone:"Trimbakeshwar",  officers:2, required:2, gap:0,  coverage:100,risk:"high"     },
  { zone:"Nashik Road Stn",officers:2, required:2, gap:0,  coverage:100,risk:"moderate" },
  { zone:"Kalaram Temple", officers:1, required:1, gap:0,  coverage:100,risk:"moderate" },
  { zone:"Gangapur Camp",  officers:1, required:1, gap:0,  coverage:100,risk:"normal"   },
];

const HANDOVER = [
  { priority:"P1", item:"INC-8821 — Crowd surge Ramkund Entry 2",    status:"Open",    toOfficer:"UNIT-P24 (Shift B)", note:"Diversion active — monitor Entry 4 flow" },
  { priority:"P1", item:"INC-8819 — Ambulance block Camp Beta",       status:"Escalated",toOfficer:"UNIT-H06 (Shift B)",      note:"Police escort requested — DCP approved"  },
  { priority:"P2", item:"MP-1185 — Missing child Panchvati Sec 7",   status:"Active",  toOfficer:"UNIT-P17 (Shift B)",  note:"CCTV search in progress Sectors 6–8"     },
  { priority:"P2", item:"Camp 3 Shelter overflow → Camp Delta",       status:"Pending", toOfficer:"Revenue Officer (Shift B)",note:"Delta activation ETA 16:00"              },
  { priority:"P3", item:"Water tanker TK-12 — ETA 15:12 Station 8",  status:"In Transit",toOfficer:"UNIT-M41 (Shift B)",     note:"Monitor arrival and log confirmation"    },
];

const INCIDENT_DETAIL_EXT = {
  "INC-8821": {
    confidenceFactors:[
      { factor:"Zone Density Score",  weight:32, score:94 },
      { factor:"Historical Pattern",  weight:28, score:89 },
      { factor:"Time-of-Day Risk",    weight:20, score:96 },
      { factor:"Weather Adjustment",  weight:12, score:84 },
      { factor:"Resource Availability",weight:8, score:88 },
    ],
    resources:["2× SRP Platoon deployed","Police Van PV-08","Crowd Diversion Barrier Set B","Medical Standby — AM-01"],
    checklist:[false, true, false, false, false],
  },
  "INC-8820": {
    confidenceFactors:[
      { factor:"Service Stress Level",  weight:40, score:92 },
      { factor:"Historical Pattern",    weight:25, score:84 },
      { factor:"Logistics Data",        weight:20, score:88 },
      { factor:"Weather Adjustment",    weight:15, score:78 },
    ],
    resources:["NMC Tanker TK-12 (in transit)","PA System PA-07","Water Station Monitor"],
    checklist:[false, false, false, false],
  },
  "INC-8819": {
    confidenceFactors:[
      { factor:"Medical Urgency Score", weight:45, score:98 },
      { factor:"Route Blockage AI",     weight:30, score:96 },
      { factor:"Resource Proximity",    weight:15, score:88 },
      { factor:"Historical Pattern",    weight:10, score:92 },
    ],
    resources:["Ambulance AM-03 (blocked)","Police Escort PQ-01 (requested)","NDRF Unit 2 (standby)"],
    checklist:[false, false, false, false],
  },
};

// ── helpers ───────────────────────────────────────────────────────
const severityStyle = s =>
  s === "CRITICAL" ? "border-red-200 bg-red-50/30" :
  s === "HIGH"     ? "border-orange-200 bg-orange-50/20" : "border-amber-200 bg-amber-50/20";

const priorityClass = p =>
  p === "CRITICAL" ? "priority-critical" : p === "HIGH" ? "priority-high" : "priority-medium";

const statusBadge = s =>
  s === "Emergency"    ? "badge badge-critical" :
  s === "Responding"   ? "badge badge-high" :
  s === "In Progress"  ? "badge badge-warning" :
  s === "Active"       ? "badge badge-high" :
  s === "Resolved"     ? "badge badge-success" :
  s === "Escalated"    ? "badge badge-critical" : "badge badge-success";

const commColor = t =>
  t === "ai"      ? "bg-violet-50 border-violet-200" :
  t === "command" ? "bg-blue-50 border-blue-200"    :
  t === "field"   ? "bg-slate-50 border-slate-200"  : "bg-emerald-50 border-emerald-200";

const commIcon = t =>
  t === "ai"      ? Brain :
  t === "command" ? Radio :
  t === "field"   ? Mic   : Send;

const SLA_BANDS = [ { min:40, bucket:"info" }, { min:20, bucket:"warning" }, { min:0, bucket:"critical" } ];
const URGENCY_BUCKET = { HIGH:"critical", MEDIUM:"warning" };
const RISK_LEVEL_BUCKET = { critical:"critical", high:"serious", moderate:"warning" };
const COVERAGE_BANDS = [ { min:100, bucket:"good" }, { min:80, bucket:"warning" }, { min:0, bucket:"critical" } ];
const SLA_HIT_BANDS = [ { min:95, bucket:"good" }, { min:85, bucket:"info" }, { min:0, bucket:"warning" } ];
const INCIDENT_COUNT_BANDS = [ { min:3, bucket:"critical" }, { min:1, bucket:"warning" }, { min:0, bucket:"good" } ];
const HANDOVER_PRIORITY_BUCKET = { P1:"critical", P2:"warning" };
const HANDOVER_STATUS_BUCKET = { Open:"serious", Active:"serious", Escalated:"critical", Pending:"warning" };

// Every zone this page names also has a live density/risk read on Crowd
// Intelligence — link to it so "Ramkund Ghat" here isn't a dead label.
const ZONE_SLUG = {
  "Ramkund Ghat":"Ramkund", "Panchvati":"Panchvati", "Trimbakeshwar":"Trimbak",
  "Nashik Road Stn":"Nashik Rd", "Kalaram Temple":"Kalaram", "Gangapur Camp":"Gangapur",
};
const ZoneLink = ({ zone, children }) => {
  const slug = ZONE_SLUG[zone];
  if (!slug) return <>{children}</>;
  return (
    <Link to={`/crowd-intelligence?zone=${encodeURIComponent(slug)}`}
      className="hover:text-blue-700 hover:underline decoration-dotted underline-offset-2">
      {children}
    </Link>
  );
};

// ── component ─────────────────────────────────────────────────────
export default function FieldOps() {
  const { live: liveModel, twin } = useIntelligence({ running: false });
  const t = useTranslate();
  const [expanded, setExpanded]     = useState(null);
  const [sopChecked, setSopChecked] = useState({});
  const [taskTab, setTaskTab]       = useState("inProgress");
  const [activeTab, setActiveTab]   = useState("performance");

  const toggleSop = (alertId, idx) => {
    const key = `${alertId}-${idx}`;
    setSopChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Computed from the real arrays rendered below, not hand-typed — so these
  // can't silently drift from the tables/feeds they summarise.
  const openIncidentCount   = SLA_TRACKER.filter(s => s.status !== "Resolved").length;
  const slaAtRiskCount      = SLA_TRACKER.filter(s => s.minsLeft < 20 && s.status !== "Resolved").length;
  const avgAiConfidence     = Math.round(
    fieldOfficerAlerts.reduce((sum, a) => sum + a.aiConfidence, 0) / fieldOfficerAlerts.length
  );
  const tasksTodayShown = TASK_BOARD.assigned.length + TASK_BOARD.inProgress.length +
    TASK_BOARD.pendingEscalation.length + TASK_BOARD.closed.length;

  return (
    <div className="space-y-8">
      <SectionHeader icon="ClipboardList" title="Field Officer Decision Copilot"
        subtitle="360° field intelligence · AI SOP engine · SLA tracking · task board · zone coverage · comms log · shift performance · handover brief"
      />


      <LiveCopilotSection live={liveModel} twin={twin} />

      <StaticContentDivider />
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard title="Open Incidents"   value={openIncidentCount.toString()}      icon="AlertCircle"  color="red"    index={0} live />
        <MetricCard title="Officers Active"  value="1,120"  icon="Users"        color="blue"   index={1} />
        <MetricCard title="SLA at Risk"      value={slaAtRiskCount.toString()}         icon="Clock"        color="amber"  index={2} live />
        <MetricCard title="Tasks Today"      value="49"     icon="CheckCircle"  color="green"  index={3} />
        <MetricCard title="AI Confidence"    value={`${avgAiConfidence}%`}             icon="Brain"        color="purple" index={4} />
        <MetricCard title="Avg Response"     value="8.4 m"  icon="Zap"         color="green"  index={5} />
      </div>

      {/* AI Copilot Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 bg-saffron-light rounded-2xl flex items-center justify-center flex-shrink-0">
            <Brain size={22} className="text-saffron-dark" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-black text-slate-900">{t("Maha Kumbh AI Decision Copilot")}</span>
              <span className="badge badge-live text-[9px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />{t("Active")}</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">
              {t("Generates prioritised SOPs per incident from zone density, historical patterns, resource availability and time-of-day risk. Issues proactive recommendations before incidents escalate. All actions require human officer approval — no autonomous enforcement.")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
            {[
              { label:"SOP Accuracy", val:"91%"    },
              { label:"Proactive Recs",val:"6 live" },
              { label:"Human-in-Loop", val:"100%"   },
            ].map((s, i) => {
              const c = googleColor(i);
              return (
                <div key={s.label} className="rounded-xl px-3 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background: c.bgLight, border: `1px solid ${c.border}` }}>
                  <div className="text-base font-black" style={{ color: c.text }}>{s.val}</div>
                  <div className="text-[9px] mt-0.5" style={{ color: c.text }}>{t(s.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Incident Copilot Feed */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Active Incident Copilot Feed")}</h3>
        <div className="space-y-4">
          {fieldOfficerAlerts.map((alert, i) => {
            const isOpen  = expanded === alert.id;
            const ext     = INCIDENT_DETAIL_EXT[alert.id] || {};
            const totalSop = alert.sop.length;
            const doneCount = alert.sop.filter((_, idx) => sopChecked[`${alert.id}-${idx}`]).length;
            return (
              <motion.div key={alert.id}
                initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`card overflow-hidden border transition-all ${severityStyle(alert.severity)} ${isOpen ? "shadow-card-hover" : ""}`}
              >
                {/* Incident header */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[9px] font-black text-slate-400 font-mono">{alert.id}</span>
                        <span className={priorityClass(alert.severity)}>{t(alert.severity)}</span>
                        <span className={statusBadge(alert.status)}>{t(alert.status)}</span>
                      </div>
                      <h4 className="text-sm font-black text-slate-900 leading-snug">{t(alert.issue)}</h4>
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin size={9} />{t(alert.location)}</span>
                        <span className="flex items-center gap-1"><User size={9} /><strong>{t(alert.owner)}</strong></span>
                      </div>
                      {/* SOP progress bar */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-1.5 rounded-full bg-emerald-500 transition-all"
                            style={{ width:`${(doneCount / totalSop) * 100}%` }} />
                        </div>
                        <span className="text-[9px] text-slate-500">{doneCount}/{totalSop} {t("SOP steps done")}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className={`text-xs font-black flex items-center gap-1 ${
                        SLA_TRACKER.find(s => s.id === alert.id)?.minsLeft < 20 ? "text-red-600 animate-pulse" : "text-amber-600"
                      }`}>
                        <Clock size={11} />{t("SLA")} {alert.escalation}
                      </div>
                      <div className="text-[10px] text-violet-600 font-semibold flex items-center gap-1">
                        <Brain size={10} />{alert.aiConfidence}% {t("confidence")}
                      </div>
                      <button onClick={() => setExpanded(isOpen ? null : alert.id)}
                        className="btn-ghost text-xs flex items-center gap-1">
                        {isOpen ? <><ChevronUp size={12} /> {t("Collapse")}</> : <><ChevronDown size={12} /> {t("View Full SOP")}</>}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded SOP */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.25 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 bg-slate-50 space-y-5">

                        {/* AI SOP Steps */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Brain size={14} className="text-violet-600" />
                            <span className="text-xs font-black text-slate-900">{t("AI Recommended SOP")}</span>
                            <span className="badge badge-purple text-[9px]">{t("Human Approval Required")}</span>
                          </div>
                          <div className="space-y-2">
                            {alert.sop.map((step, idx) => {
                              const done = !!sopChecked[`${alert.id}-${idx}`];
                              return (
                                <div key={idx}
                                  onClick={() => toggleSop(alert.id, idx)}
                                  className={`flex items-start gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${
                                    done ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-blue-200"
                                  }`}
                                >
                                  <span className={`w-5 h-5 rounded-full text-[9px] font-black flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                    done ? "bg-emerald-500 text-white" : "gov-gradient text-white"
                                  }`}>
                                    {done ? <CheckCircle size={11} /> : idx + 1}
                                  </span>
                                  <span className={`text-xs leading-relaxed ${done ? "line-through text-slate-400" : "text-slate-700"}`}>
                                    {t(step)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* AI Confidence Factors */}
                        {ext.confidenceFactors && (
                          <div>
                            <div className="text-xs font-black text-slate-800 mb-2">{t("AI Confidence Breakdown")}</div>
                            <div className="space-y-1.5">
                              {ext.confidenceFactors.map(f => (
                                <div key={f.factor} className="flex items-center gap-2">
                                  <span className="text-[10px] text-slate-600 w-40 flex-shrink-0">{t(f.factor)}</span>
                                  <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                    <div className="h-1.5 rounded-full bg-violet-500" style={{ width:`${f.score}%` }} />
                                  </div>
                                  <span className="text-[10px] font-black text-violet-700 w-8 text-right">{f.score}%</span>
                                  <span className="text-[9px] text-slate-400 w-12 text-right">{t("wt")}:{f.weight}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Resources assigned */}
                        {ext.resources && (
                          <div>
                            <div className="text-xs font-black text-slate-800 mb-2">{t("Assigned Resources")}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {ext.resources.map((r, j) => (
                                <span key={j} className="badge badge-info text-[9px]">{t(r)}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                          <button className="btn-primary text-xs flex items-center gap-1.5 flex-1">
                            <CheckCircle size={12} /> {t("Approve & Execute SOP")}
                          </button>
                          <button className="btn-secondary text-xs flex items-center gap-1.5">
                            <TrendingUp size={12} /> {t("Escalate to Senior")}
                          </button>
                          <button className="btn-secondary text-xs flex items-center gap-1.5">
                            <FileText size={12} /> {t("Modify SOP")}
                          </button>
                          <button className="btn-secondary text-xs flex items-center gap-1.5">
                            <Radio size={12} /> {t("Radio Officer")}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* SLA Tracker */}
      <ChartCard title="SLA Tracker — All Open Incidents"
        subtitle="Remaining time to SLA breach per incident · red = breach imminent">
        <div className="space-y-2">
          {SLA_TRACKER.map((s, i) => {
            const pct = s.minsLeft === 999 ? 100 :
              Math.max(0, Math.min(100, Math.round((s.minsLeft / 90) * 100)));
            const barColor = s.status === "Resolved" ? statusHex("good") : statusHex(thresholdStatus(s.minsLeft, SLA_BANDS));
            return (
              <motion.div key={s.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  s.status === "Resolved"  ? "border-emerald-200 bg-emerald-50/30" :
                  s.minsLeft < 20          ? "border-red-200 bg-red-50/30 animate-pulse" :
                  s.minsLeft < 40          ? "border-amber-200 bg-amber-50/20" : "border-slate-200"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-[9px] font-mono text-slate-400">{s.id}</span>
                    <span className={priorityClass(s.priority)}>{t(s.priority)}</span>
                  </div>
                  <div className="text-xs font-bold text-slate-800 truncate">{t(s.issue)}</div>
                  <div className="text-[9px] text-slate-500 mt-0.5">{t(s.owner)}</div>
                </div>
                <div className="w-28 flex-shrink-0">
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1">
                    <div className="h-2 rounded-full transition-all" style={{ width:`${pct}%`, background:barColor }} />
                  </div>
                  <div className={`text-[10px] font-black text-right ${
                    s.status === "Resolved" ? "text-emerald-600" :
                    s.minsLeft < 20 ? "text-red-600" : "text-slate-700"
                  }`}>
                    {s.status === "Resolved" ? t("✓ Done") : `${s.minsLeft} ${t("min left")}`}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className={statusBadge(s.status)}>{t(s.status)}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </ChartCard>

      {/* AI Proactive Recommendations */}
      <ChartCard title="AI Proactive Recommendation Feed"
        subtitle="Real-time AI-generated field actions before incidents escalate · confidence-ranked">
        <div className="space-y-2">
          {AI_RECOMMENDATIONS.map((r, i) => (
            <motion.div key={r.id}
              initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
              className={`flex items-start gap-3 p-3 rounded-xl border ${
                r.urgency === "HIGH"   ? "border-red-200 bg-red-50/20"    :
                r.urgency === "MEDIUM" ? "border-amber-200 bg-amber-50/20" : "border-slate-200"
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                r.type === "Proactive" ? "bg-violet-100" : r.type === "Reactive" ? "bg-red-100" : "bg-blue-100"
              }`}>
                <Brain size={14} className={
                  r.type === "Proactive" ? "text-violet-600" : r.type === "Reactive" ? "text-red-600" : "text-blue-600"
                } />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-[9px] font-black text-slate-400">{r.id}</span>
                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                    r.type === "Proactive" ? "bg-violet-100 text-violet-700" :
                    r.type === "Reactive"  ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                  }`}>{t(r.type)}</span>
                  <span className="badge badge-info text-[8px]">{t(r.zone)}</span>
                  <span className="text-[9px] text-violet-600 font-bold">AI: {r.confidence}%</span>
                </div>
                <p className="text-xs text-slate-700 font-semibold leading-snug">{t(r.msg)}</p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(URGENCY_BUCKET[r.urgency] || "neutral")}`}>{t(r.urgency)}</span>
                <button className="text-[9px] font-black text-blue-700 hover:text-blue-800 mt-1">{t(r.action)} →</button>
              </div>
            </motion.div>
          ))}
        </div>
      </ChartCard>

      {/* Task Board */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-1">{t("Task Management Board")}</h3>
        <p className="text-[10px] text-slate-400 mb-3">
          {t("Showing")} {tasksTodayShown} {t("of")} 49 {t("tasks today — earlier-shift and routine tasks are in the full shift log, not this live board")}
        </p>
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { id:"inProgress",       labelKey:"In Progress",        count: TASK_BOARD.inProgress.length        },
            { id:"assigned",         labelKey:"Assigned",           count: TASK_BOARD.assigned.length          },
            { id:"pendingEscalation",labelKey:"Pending Escalation", count: TASK_BOARD.pendingEscalation.length },
            { id:"closed",           labelKey:"Closed Today",       count: TASK_BOARD.closed.length            },
          ].map(tab => (
            <button key={tab.id} onClick={() => setTaskTab(tab.id)}
              className={`text-xs font-black px-4 py-2 rounded-xl transition-colors ${
                taskTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>{t(tab.labelKey)} ({tab.count})</button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={taskTab} initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
            <div className="space-y-2">
              {TASK_BOARD[taskTab].map((task, i) => (
                <motion.div key={task.id}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${
                    taskTab === "closed"           ? "border-emerald-200 bg-emerald-50/20" :
                    taskTab === "pendingEscalation"? "border-red-200 bg-red-50/20"         :
                    task.priority === "CRITICAL"   ? "border-red-200 bg-red-50/20"         :
                    task.priority === "HIGH"       ? "border-orange-200 bg-orange-50/20"   : "border-slate-200"
                  }`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    taskTab === "closed" ? "bg-emerald-100" : "bg-blue-100"
                  }`}>
                    {taskTab === "closed"
                      ? <CheckCircle size={14} className="text-emerald-600" />
                      : <Target size={14} className="text-blue-600" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-[9px] font-mono text-slate-400">{task.id}</span>
                      <span className={priorityClass(task.priority)}>{t(task.priority)}</span>
                      <span className="badge badge-info text-[8px]">{t(task.dept)}</span>
                    </div>
                    <p className={`text-xs font-semibold ${taskTab === "closed" ? "line-through text-slate-400" : "text-slate-800"}`}>{t(task.task)}</p>
                  </div>
                  <div className="text-right flex-shrink-0 text-[10px] text-slate-500">
                    <div className="font-semibold text-slate-700">{t(task.assignee)}</div>
                    <div className="flex items-center gap-0.5 justify-end mt-0.5">
                      <Clock size={8} />{task.due === "Done" ? t("Done") : task.due}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Zone Coverage + Officer Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Zone Officer Coverage" subtitle="Officers deployed vs required per zone">
          <div className="space-y-3">
            {ZONE_COVERAGE.map((z, i) => (
              <motion.div key={z.zone}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700"><ZoneLink zone={z.zone}>{t(z.zone)}</ZoneLink></span>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className={`font-bold ${z.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {z.officers}/{z.required} {t("officers")}
                    </span>
                    {z.gap > 0 && <span className="text-red-600 font-black">–{z.gap} {t("gap")}</span>}
                  </div>
                </div>
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-3 rounded-full" style={{
                    background: statusHex(thresholdStatus(z.coverage, COVERAGE_BANDS))
                  }}
                    initial={{ width:0 }} animate={{ width:`${z.coverage}%` }}
                    transition={{ duration:0.8, delay: i * 0.06 }}
                  />
                </div>
                <div className="flex items-center justify-between mt-0.5 text-[9px]">
                  <span className={z.gap > 0 ? "text-red-600 font-bold" : "text-slate-500"}>
                    {z.coverage}% {t("coverage")} {z.gap > 0 ? t("— reinforcement needed") : ""}
                  </span>
                  <span className={`font-black px-1.5 py-0.5 rounded-full text-[8px] ${statusClasses(RISK_LEVEL_BUCKET[z.risk] || "good")}`}>{t(z.risk.toUpperCase())}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Communications Log"
          subtitle="Live field radio, AI alerts and command broadcasts">
          <div className="space-y-2 max-h-[340px] overflow-y-auto pr-0.5">
            {COMMS_LOG.map((c, i) => {
              const Icon = commIcon(c.type);
              return (
                <motion.div key={i}
                  initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
                  className={`flex items-start gap-2.5 p-2.5 rounded-xl border text-xs ${commColor(c.type)}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    c.type === "ai"      ? "bg-violet-100" :
                    c.type === "command" ? "bg-blue-100"   : "bg-slate-100"
                  }`}>
                    <Icon size={11} className={
                      c.type === "ai"      ? "text-violet-600" :
                      c.type === "command" ? "text-blue-600"   : "text-slate-600"
                    } />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-0.5 flex-wrap">
                      <span className="font-black text-slate-800 text-[10px]">{t(c.from)}</span>
                      <span className="text-[9px] text-slate-400 font-mono">{c.time}</span>
                    </div>
                    <p className="text-slate-700 text-[10px] leading-snug">{t(c.msg)}</p>
                    <span className="text-[8px] text-slate-400 mt-0.5 block">{t(c.channel)}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* Officer Performance Table */}
      <div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { id:"performance", label:"Officer Performance" },
            { id:"resources",   label:"Resource Deployment" },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`text-xs font-black px-4 py-2 rounded-xl transition-colors ${
                activeTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>{t(tab.label)}</button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          {activeTab === "performance" && (
            <motion.div key="performance" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ChartCard title="Field Officer Performance Dashboard"
                subtitle="Tasks, SLA hit rate, active incidents and radio channel per officer">
                <div className="overflow-x-auto">
                  <table className="gov-table">
                    <thead>
                      <tr>
                        <th>{t("Officer")}</th><th>{t("Rank")}</th><th>{t("Zone")}</th>
                        <th>{t("Tasks Today")}</th><th>{t("Completed")}</th><th>{t("SLA Hit %")}</th>
                        <th>{t("Incidents")}</th><th>{t("Radio")}</th><th>{t("Status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {OFFICER_PERFORMANCE.map((o, i) => (
                        <motion.tr key={o.unit} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                          <td>
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0 ${
                                o.status === "Emergency"  ? "bg-red-100 text-red-700" :
                                o.status === "Responding" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
                              }`}>{o.unit.slice(-2)}</div>
                              <span className="font-bold text-slate-900">{o.unit}</span>
                            </div>
                          </td>
                          <td className="text-slate-500 text-xs">{t(o.rank)}</td>
                          <td><span className="flex items-center gap-1 text-xs"><MapPin size={9} className="text-blue-600" /><ZoneLink zone={o.zone}>{t(o.zone)}</ZoneLink></span></td>
                          <td className="font-semibold text-center">{o.tasksToday}</td>
                          <td className="font-semibold text-center text-emerald-600">{o.completed}</td>
                          <td>
                            <div className="flex items-center gap-1.5">
                              <div className="w-12 h-1.5 rounded-full bg-slate-100">
                                <div className="h-1.5 rounded-full" style={{
                                  width:`${o.slaHit}%`,
                                  background: statusHex(thresholdStatus(o.slaHit, SLA_HIT_BANDS))
                                }} />
                              </div>
                              <span className="text-[10px] font-bold">{o.slaHit}%</span>
                            </div>
                          </td>
                          <td className="font-black text-center" style={{ color: statusHex(thresholdStatus(o.incidents, INCIDENT_COUNT_BANDS)) }}>
                            {o.incidents}
                          </td>
                          <td className="text-xs text-blue-600 font-bold">{o.radio}</td>
                          <td><span className={statusBadge(o.status)}>{t(o.status)}</span></td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            </motion.div>
          )}
          {activeTab === "resources" && (
            <motion.div key="resources" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
              <ChartCard title="Zone Resource Deployment" subtitle="Police, health, volunteers, ambulances and fire units per zone">
                <div className="overflow-x-auto">
                  <table className="gov-table">
                    <thead>
                      <tr><th>{t("Zone")}</th><th>{t("Police")}</th><th>{t("Health")}</th><th>{t("Volunteers")}</th><th>{t("Ambulance")}</th><th>{t("Fire")}</th><th>{t("Status")}</th></tr>
                    </thead>
                    <tbody>
                      {resourceDeployment.map((r, i) => (
                        <motion.tr key={r.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                          <td className="font-bold"><ZoneLink zone={r.zone}>{t(r.zone)}</ZoneLink></td>
                          <td className="text-blue-700 font-semibold">{r.police.toLocaleString()}</td>
                          <td className="text-emerald-700 font-semibold">{r.health}</td>
                          <td className="text-amber-700 font-semibold">{r.volunteers}</td>
                          <td className="text-red-700 font-bold">{r.ambulance}</td>
                          <td>{r.fire}</td>
                          <td>
                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(RISK_LEVEL_BUCKET[r.status] || "good")}`}>{t(r.status.toUpperCase())}</span>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Shift Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Shift Performance — Tasks & Incidents" subtitle="Hourly task completion vs incidents raised today" live>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={SHIFT_PERFORMANCE} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="completed" fill={statusHex("good")} radius={[3,3,0,0]} name={t("Tasks Completed")} />
              <Bar dataKey="incidents" fill={statusHex("critical")} radius={[3,3,0,0]} name={t("Incidents Raised")} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="SLA Compliance Rate — Hourly" subtitle="% of incidents resolved within SLA per hour" live>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={SHIFT_PERFORMANCE} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} unit="%" domain={[75, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="sla" stroke={chartColor(0)} strokeWidth={2.5} dot={{ r:3 }} name={t("SLA Hit %")} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-lg font-black text-blue-700">49</div><div className="text-[10px] text-slate-500">{t("Tasks Today")}</div></div>
            <div><div className="text-lg font-black text-emerald-600">44</div><div className="text-[10px] text-slate-500">{t("Completed")}</div></div>
            <div><div className="text-lg font-black text-amber-600">91.4%</div><div className="text-[10px] text-slate-500">{t("Avg SLA Hit")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Escalation Workflow + Shift Handover */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Escalation Workflow" subtitle="Incident escalation path by severity level and time">
          <div className="flex flex-col gap-2 mb-4">
            {[
              { role:"Field Officer",            level:"L1", color:"bg-slate-100",  text:"text-slate-700",  border:"border-slate-200",  time:"T+0 — First response"        },
              { role:"Inspector / PSI",           level:"L2", color:"bg-blue-50",    text:"text-blue-800",   border:"border-blue-200",    time:"T+5 min — if unresolved"     },
              { role:"DCP / CMO / Collector",    level:"L3", color:"bg-amber-50",   text:"text-amber-800",  border:"border-amber-200",   time:"T+10 min — HIGH priority"    },
              { role:"SP / District Collector",  level:"L4", color:"bg-orange-50",  text:"text-orange-800", border:"border-orange-200",  time:"T+15 min — CRITICAL only"    },
              { role:"CM Office / CS",           level:"L5", color:"bg-red-50",     text:"text-red-800",    border:"border-red-200",     time:"T+20 min — State-level threat"},
            ].map((step, i) => (
              <div key={step.level} className="flex items-center gap-2">
                <div className={`rounded-xl border ${step.border} ${step.color} px-3 py-2 flex-1`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold ${step.text}`}>{t(step.role)}</span>
                    <span className="text-[9px] font-black text-slate-400">{step.level}</span>
                  </div>
                  <div className="text-[9px] text-slate-500 mt-0.5">{t(step.time)}</div>
                </div>
                {i < 4 && <div className="text-white/60 text-base">↓</div>}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center">
            <div><span className="badge badge-critical text-[8px]">{t("CRITICAL")}</span><p className="text-[9px] text-slate-500 mt-1">{t("Escalates in 5 min")}</p></div>
            <div><span className="badge badge-high text-[8px]">{t("HIGH")}</span><p className="text-[9px] text-slate-500 mt-1">{t("Escalates in 10 min")}</p></div>
            <div><span className="badge badge-info text-[8px]">{t("LOW/MED")}</span><p className="text-[9px] text-slate-500 mt-1">{t("Self-managed")}</p></div>
          </div>
        </ChartCard>

        <ChartCard title="Shift Handover Brief"
          subtitle="Items to hand over to Shift B — AI-generated briefing for next shift">
          <div className="space-y-2.5">
            {HANDOVER.map((h, i) => (
              <motion.div key={i}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className={`p-3 rounded-xl border ${
                  h.priority === "P1" ? "border-red-200 bg-red-50/30"    :
                  h.priority === "P2" ? "border-amber-200 bg-amber-50/20": "border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(HANDOVER_PRIORITY_BUCKET[h.priority] || "info")}`}>{h.priority}</span>
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(HANDOVER_STATUS_BUCKET[h.status] || "info")}`}>{t(h.status)}</span>
                    </div>
                    <p className="text-xs font-bold text-slate-800">{t(h.item)}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{t(h.note)}</p>
                  </div>
                  <div className="text-right flex-shrink-0 text-[9px] text-blue-700 font-semibold max-w-[120px]">
                    → {t(h.toOfficer)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-xl text-[10px] text-blue-700 font-semibold">
            {t("AI-generated handover brief · Shift B start time: 16:00 IST · All P1 items require verbal confirmation")}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}





